import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createGlobalState } from 'react-hooks-global-state'
import { Editor } from '@tiptap/react'
import { addSeconds } from 'date-fns'
import { useEffectOnce } from 'react-use'

import { AppState } from '../../store'
import { Snippet } from '../../store/templates/types'
import { currentMessageUpdate, currentMessageReset } from '../../store/currentMessage/actions'
import { applyTemplateToCurrentMessage } from '../../store/templates/actions'
import { htmlHasPlaceholder, htmlHasText, htmlToPlainText } from '../../utils/richtext'
import { HandlebarsContextType, renderEmailTemplate } from '../../utils/handlebars'
import { Template } from '../../store/templates/types'
import { CurrentMessage } from '../../store/currentMessage/types'

export const MessageDraftStorageKey = 'message-draft'

interface UseEditorState {
  editorHtml: string
  setEditorHtml: (html: string) => void
  syncEditorState: (updatedHtml?: string) => void
  insertSnippetAtCursor: (snippet: Snippet, handlebarsContext: HandlebarsContextType) => void
  insertTemplate: (template: Template, handlebarsContext: HandlebarsContextType) => Promise<void>
  clearEditorState: (effectRedux?: boolean) => void
  /** Registered by the editor component so snippets can be inserted at the caret. */
  editorRef: React.MutableRefObject<Editor | null>
  hasText: boolean
  hasPlaceholderText: boolean
}

// Could I have saved a dependency and rolled my own context provider? Yes, but my brain is mush and the API around this
// was so much easier than rolling my own.
const { GlobalStateProvider, useGlobalState } = createGlobalState({
  editorHtml: '',
})

/**
 * Shared across every useEditorState() consumer.
 *
 * This must NOT be a useRef: each component calling the hook would get its own
 * ref, and only the one rendering the editor would ever have it populated. The
 * others (StrategyContainer inserting a snippet, the composition window's CTA
 * sync) would silently see null. There is one composer at a time, so a module
 * level ref is the right scope.
 *
 * Lifecycle lives in RichTextEditor: it registers the editor here in an effect
 * and clears it again on unmount, so this never holds a destroyed instance.
 */
const sharedEditorRef: { current: Editor | null } = { current: null }

export const EditorStateProvider = GlobalStateProvider

/**
 * useEditorState is a React hook that provides utilities for working with the message body in the context of our
 * application.
 *
 * A little bit of background: Once upon a time, we were syncing the editor content to Redux on each keypress. This
 * worked fine for a bit, but we eventually ran into performance issues because large parts of the app were
 * re-rendering on each keypress. In an effort to avoid this, we moved the state so that it's local to the component
 * and shared via a context provider. We still have a bunch of thunks that use the currentMessage store and we don't
 * want to rewrite that and we need to sync to it, so we sync to Redux in the hook when the editor loses focus.
 *
 * The body is held as HTML. It used to be a draft-js EditorState, but draft-js pinned us to a vulnerable immutable 3.x
 * (CCB-5482) so the editor is now TipTap. HTML is what the API wants anyway, so the conversion step is gone.
 */
export const useEditorState = (): UseEditorState => {
  const dispatch = useDispatch()
  const currentMessageState = useSelector<AppState, CurrentMessage>(state => state.currentMessageReducer)
  const [editorHtml, updateEditorHtml] = useGlobalState('editorHtml')
  const [isFirstRender, setIsFirstRender] = useState(true)
  const [lastSynced, setLastSynced] = useState<Date | undefined>()
  const hasText = htmlHasText(editorHtml)
  const hasPlaceholderText = htmlHasPlaceholder(editorHtml)

  const setEditorHtml = useCallback(
    (newHtml: string) => {
      // Pasted content no longer needs filtering here: the TipTap schema only permits bold, italic and lists, so
      // ProseMirror drops anything else while parsing the paste.
      updateEditorHtml(newHtml)

      if ((!lastSynced || lastSynced < addSeconds(new Date(), -20)) && htmlHasText(newHtml)) {
        localStorage.setItem(
          MessageDraftStorageKey,
          JSON.stringify({
            ...currentMessageState,
            editorHtml: newHtml,
          })
        )
        setLastSynced(new Date())
      }
    },
    // We don't want it to run every time when currentMessageState changes
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [updateEditorHtml, lastSynced, setLastSynced]
  )

  const syncEditorState = useCallback(
    (updatedHtml?: string) =>
      dispatch(
        currentMessageUpdate({
          editorHtml: updatedHtml !== undefined ? updatedHtml : editorHtml,
        })
      ),
    [dispatch, editorHtml]
  )

  const insertSnippetAtCursor = useCallback(
    (snippet: Snippet, handlebarsContext: HandlebarsContextType) => {
      const renderedSnippetText = renderEmailTemplate(snippet.body, handlebarsContext)
      const editor = sharedEditorRef.current

      if (!editor) {
        return
      }

      editor.chain().focus().insertContent(renderedSnippetText).run()
      const updatedHtml = editor.getHTML()
      updateEditorHtml(updatedHtml)
      syncEditorState(updatedHtml)
    },
    [updateEditorHtml, syncEditorState]
  )

  const insertTemplate = useCallback(
    async (template: Template, handlebarsContext: HandlebarsContextType) => {
      const action = applyTemplateToCurrentMessage(template, handlebarsContext)

      if (action.messageUpdate.editorHtml !== undefined) {
        updateEditorHtml(action.messageUpdate.editorHtml)
      }

      await dispatch(action)
    },
    [dispatch, updateEditorHtml]
  )

  const clearEditorState = useCallback(
    async (effectRedux = true) => {
      if (effectRedux) {
        await dispatch(currentMessageReset())
      }

      setEditorHtml('')
    },
    [setEditorHtml, dispatch]
  )

  // If the Redux content is much larger than the content in the local state, assume that a CTA key press changed it and
  // switch to it. This is a terrible hack for when the message composition window is open and the user presses a CTA.
  // This can't be done "the right way" because it leads to re-render hell if I were to wrap the whole message
  // composition window with the EditorStateProvider. We will only do this outside of the first render AND if the editor
  // doesn't have focus.
  // @TODO Convert this to event listening logic as this is hacky as hell
  useEffect(() => {
    const editor = sharedEditorRef.current
    if (!isFirstRender && !(editor && editor.isFocused)) {
      // Compare plain-text lengths, as the draft-js version did with getPlainText().
      const reduxContentLength = htmlToPlainText(currentMessageState.editorHtml).length
      const localContentLength = htmlToPlainText(editorHtml).length

      if (reduxContentLength - localContentLength >= 10) {
        setEditorHtml(currentMessageState.editorHtml)
      }
    }
  }, [editorHtml, currentMessageState, setEditorHtml, isFirstRender])

  // Check local storage for a message draft for this client before rendering the default message. If draft found, render it instead; If draft found but
  // it is for another client, remove the draft from local storage since we're only keeping track of one draft at a time in case of emergency (app crash/
  // accidental page refresh)
  useEffectOnce(() => {
    if (isFirstRender) {
      const stored = localStorage.getItem(MessageDraftStorageKey)
      const messageDraft = stored ? JSON.parse(stored) : null
      if (!!messageDraft) {
        // Drafts written before the TipTap migration hold a draft-js raw object under `editorState`; they can't be
        // rendered any more, so drop them rather than trying to convert.
        const draftHtml: string | undefined = messageDraft.editorHtml

        if (draftHtml && messageDraft.client_id && window.location.pathname.includes(messageDraft.client_id)) {
          dispatch(
            currentMessageUpdate({
              ...messageDraft,
              editorHtml: draftHtml,
            })
          )
          setEditorHtml(draftHtml)
        } else {
          localStorage.removeItem(MessageDraftStorageKey)
        }
      }
      if (htmlHasText(currentMessageState.editorHtml)) {
        setEditorHtml(currentMessageState.editorHtml)
      }
      setIsFirstRender(false)
    }
  })

  return {
    editorHtml,
    setEditorHtml,
    syncEditorState,
    insertSnippetAtCursor,
    insertTemplate,
    clearEditorState,
    editorRef: sharedEditorRef,
    hasText,
    hasPlaceholderText,
  }
}

export default useEditorState
